import { useCallback, useEffect, useMemo } from 'react';
import { Empty, Space, Spin } from 'antd';
import { useOutletContext, useParams } from 'react-router-dom';
import { ALL_ITEMS_PAGE_SIZE } from 'core/http/pagination';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useFormUtils from 'hooks/useFormUtils';
import useProducts from 'hooks/useProducts';
import { ICS2GoodsItemCategory, ICS2GoodsItemResponse } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { useProductContext } from '../../context/ProductContext';
import MasterItemListItem from './MasterItemListItem';

const MasterItems = () => {
    const { viewOnly } = useMasterItemsContext();
    const { declarationId } = useParams();
    const { countryLowerCase: country, formType, internalType } = useFormUtils();
    const { products, isLoading, listDeclarationProducts, deleteDeclarationProduct } = useProducts();
    const { setProductId } = useProductContext();
    const { declarationErrors } = useDeclarationFormErrors();

    // The parser numbers the items by their place in the declaration, which is the order they are listed in.
    const invalidItemNumbers = useMemo(
        () => new Set((declarationErrors.masterItems ?? []).map(({ index }) => index)),
        [declarationErrors.masterItems]
    );

    const masterItems = (products?.list ?? []) as ICS2GoodsItemResponse[];

    const listMasterItems = useCallback(async () => {
        if (!declarationId) return;

        return listDeclarationProducts({
            declarationId,
            formType,
            internalType,
            country,
            params: { size: ALL_ITEMS_PAGE_SIZE, category: ICS2GoodsItemCategory.MASTER },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declarationId, formType, internalType, country]);

    // Being back on the list means no item is being edited any more.
    useEffect(() => {
        setProductId?.(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        listMasterItems();
    }, [listMasterItems]);

    const handleDelete = useCallback(
        async (productId: string) => {
            if (!declarationId) return;

            await deleteDeclarationProduct({ productId, declarationId, formType, internalType, country });
            await listMasterItems();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [declarationId, formType, internalType, country, listMasterItems]
    );

    if (isLoading && !masterItems.length)
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 50,
                }}
            >
                <Spin size="large" tip="Loading master items..." />
            </div>
        );

    if (!masterItems.length) return <Empty description="No master items added yet." />;

    return (
        <Space direction="vertical" style={{ width: '100%', overflowY: 'scroll', height: '100%' }} size={16}>
            {masterItems.map((item, index) => {
                const itemNumber = (products?.pageNumber ?? 0) * (products?.pageSize ?? 0) + index + 1;

                return (
                    <MasterItemListItem
                        viewOnly={viewOnly}
                        key={item.id}
                        id={item.id!}
                        itemNumber={itemNumber}
                        hasErrors={invalidItemNumbers.has(itemNumber)}
                        description={item.commodity?.descriptionOfGoods}
                        hsCode={item.commodity?.commodityCode?.harmonizedSystemSubHeadingCode}
                        grossMass={item.weight?.grossMass}
                        packagesType={item.packaging
                            ?.map(({ typeOfPackages }) => typeOfPackages)
                            .filter(Boolean)
                            .join(', ')}
                        onDelete={handleDelete}
                    />
                );
            })}
        </Space>
    );
};

export default MasterItems;

function useMasterItemsContext() {
    return useOutletContext<{
        onEdit: (id: string) => Promise<void>;
        loading?: boolean;
        viewOnly?: boolean;
    }>();
}
